/**
 * SUNTRONIX ONE — Insights section shared components.
 *
 * MetricCard: clickable headline metric with definition tooltip.
 * FilterBar:  global filter bar (date range + cascading scope).
 * StubPage:   placeholder shell every not-yet-built Insights page uses.
 * PhaseChip:  small "Phase X · coming soon" chip.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HelpCircle, ArrowUpRight, ArrowDownRight, Filter, Calendar } from "lucide-react";

const DATE_PRESETS = [
  "Today", "Yesterday", "Last 7 Days", "This Week", "This Month",
  "Previous Month", "This Quarter", "This Year", "Custom",
];

export function GlobalFilterBar({ value, onChange }) {
  const v = value || { preset: "This Month" };
  return (
    <div className="flex items-center gap-2 flex-wrap bg-white border border-slate-200 rounded-md p-2" data-testid="insights-filter-bar">
      <Calendar className="h-3.5 w-3.5 text-slate-500 ml-1" />
      <select value={v.preset} onChange={(e) => onChange({ ...v, preset: e.target.value })}
              data-testid="filter-date-preset"
              className="h-8 border border-slate-200 rounded px-2 text-[12px] bg-white">
        {DATE_PRESETS.map((p) => <option key={p}>{p}</option>)}
      </select>
      <div className="h-4 w-px bg-slate-200" />
      <Filter className="h-3.5 w-3.5 text-slate-500" />
      <span className="text-[11px] text-slate-500">
        Master client · Customer · Country · Location · Owner filters apply once records exist
      </span>
    </div>
  );
}

export function MetricCard({ metric, drillTo, testId, tone }) {
  if (!metric) return <SkeletonCard />;
  const { value, unit, delta_pct, definition, need_action, insufficient_data } = metric;
  const formatted = insufficient_data ? "—"
    : unit === "INR" ? formatINR(value)
    : unit === "%" ? `${value}%`
    : Number(value ?? 0).toLocaleString();
  const toneMap = {
    blue:   { bg: "#EFF5FF", accent: "#246BFD" },
    cyan:   { bg: "#ECFAFF", accent: "#18BCEB" },
    teal:   { bg: "#E6F7F8", accent: "#2DB7C4" },
    mint:   { bg: "#ECFDF6", accent: "#20C997" },
    amber:  { bg: "#FFF8E7", accent: "#F5A524" },
    violet: { bg: "#F5F0FF", accent: "#805AD5" },
    coral:  { bg: "#FFF0F2", accent: "#EF5A67" },
  };
  const t = toneMap[tone];
  const cardStyle = t
    ? { background: `linear-gradient(180deg, ${t.bg} 0%, #FFFFFF 60%)`,
        borderColor: "#E4E7EC" }
    : {};
  const Body = (
    <div className="border rounded-md p-4 hover:border-slate-400 transition-colors relative overflow-hidden"
         style={cardStyle}
         data-testid={testId}>
      {t && (
        <div className="absolute top-0 left-0 h-full w-1"
             style={{ background: t.accent }} />
      )}
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#667085] flex items-center gap-1">
        {metric.id?.replace(/_/g, " ")}
        <HelpCircle className="h-3 w-3 opacity-50" aria-label={definition} title={definition} />
      </div>
      <div className="mt-2 text-3xl font-semibold text-[#101828] tracking-tight"
           data-testid={`${testId}-value`}>
        {formatted}
      </div>
      <div className="mt-1 flex items-center gap-1.5 text-[11px]">
        {delta_pct != null && (
          <span className={delta_pct >= 0 ? "text-[#169C70]" : "text-[#C2333F]"}>
            {delta_pct >= 0 ? <ArrowUpRight className="h-3 w-3 inline" /> : <ArrowDownRight className="h-3 w-3 inline" />}
            {Math.abs(delta_pct)}%
          </span>
        )}
        {need_action != null && need_action > 0 && (
          <span className="st-badge st-badge-warning">
            {need_action.toLocaleString()} need action
          </span>
        )}
        {insufficient_data && <span className="text-[#98A2B3]">Insufficient data</span>}
      </div>
    </div>
  );
  return drillTo ? <Link to={drillTo} className="block">{Body}</Link> : Body;
}

function SkeletonCard() {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 animate-pulse">
      <div className="h-3 w-24 bg-slate-100 rounded" />
      <div className="mt-3 h-8 w-32 bg-slate-100 rounded" />
      <div className="mt-2 h-3 w-20 bg-slate-100 rounded" />
    </div>
  );
}

function formatINR(v) {
  if (v == null) return "—";
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
  return `₹${Math.round(v).toLocaleString()}`;
}

export function PhaseChip({ phase }) {
  return (
    <span className="inline-block text-[10px] uppercase tracking-[0.14em] bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
      Phase {phase} · scaffolding
    </span>
  );
}

/** Standard placeholder for the 15 sections we haven't fully built yet. */
export function StubPage({ title, phase, description, plannedItems = [], filterBar = true, testId }) {
  const [filter, setFilter] = useState({ preset: "This Month" });
  useEffect(() => { /* filter would drive queries here */ }, [filter]);
  return (
    <div className="space-y-4 max-w-[1400px]" data-testid={testId}>
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Insights</div>
          <PhaseChip phase={phase} />
        </div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">{title}</h1>
        <p className="text-[13px] text-slate-500 max-w-2xl">{description}</p>
      </div>
      {filterBar && <GlobalFilterBar value={filter} onChange={setFilter} />}
      <div className="bg-white border border-dashed border-slate-300 rounded-md p-8 text-center">
        <div className="text-slate-400 text-[13px]">
          This page arrives in <b>Phase {phase}</b>.  Wired into the Metric Engine.  No fabricated
          data will render until queries exist — you&apos;ll see &ldquo;Insufficient data&rdquo;.
        </div>
        {plannedItems.length > 0 && (
          <ul className="mt-4 grid grid-cols-2 gap-2 text-left max-w-2xl mx-auto">
            {plannedItems.map((it) => (
              <li key={it} className="text-[12px] text-slate-600 flex items-start gap-1.5">
                <span className="text-slate-400 mt-0.5">•</span>{it}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
